const API_BASE = (process.env.VUE_APP_API_BASE_URL || "/api/v1").replace(
  /\/$/,
  ""
);
const TRIPS_ENDPOINT = `${API_BASE}/trips?limit=25&includeRelations=false`;
const DISPLAY_ORDER_ENDPOINT = `${API_BASE}/trips/display-order`;

const assetsContext = require.context(
  "@/assets",
  true,
  /\.(png|jpe?g|webp|svg|gif)$/
);

const assetMap = assetsContext.keys().reduce((acc, key) => {
  const normalized = `@/assets${key.slice(1)}`;
  acc[normalized] = assetsContext(key);
  return acc;
}, {});

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
});

let tripsPromise = null;

export function fetchTripsData() {
  if (!tripsPromise) {
    tripsPromise = Promise.all([
      fetchJson(TRIPS_ENDPOINT),
      fetchJson(DISPLAY_ORDER_ENDPOINT).catch(() => ({ sections: [] })),
    ]).then(([tripsPayload, orderPayload]) =>
      normalizeTrips(tripsPayload.items ?? [], orderPayload.sections ?? [])
    );
  }

  return tripsPromise;
}

function fetchJson(endpoint) {
  return fetch(endpoint).then((response) => {
    if (!response.ok) {
      throw new Error(`Unable to load data from ${endpoint}`);
    }
    return response.json();
  });
}

function normalizeTrips(items, sections) {
  const normalized = items.map((item) => normalizeTrip(item));

  const tripsByCode = {};
  const tripsById = {};
  normalized.forEach((trip) => {
    if (trip.code) {
      tripsByCode[trip.code] = trip;
    }
    if (trip.id) {
      tripsById[trip.id] = trip;
    }
  });

  const pickByCodes = (codes = []) =>
    codes.map((code) => tripsByCode[code]).filter(Boolean);

  const joinAnAdventureCodes = [
    "coastToCoast",
    "caminoDeCostaRica",
    "costaricanAdventure",
    "volcanoesRiversBeaches",
    "centralAmericaMultisport",
  ];

  const familyTripCodes = [
    "activeFamilyAdventure",
    "volunteeringFamilyAdventure",
  ];

  const sectionOrdering = buildSectionOrdering(sections, tripsById);

  const defaultPromotions = normalized.filter((trip) =>
    Boolean(trip.overview.promotionText)
  );

  return {
    trips: tripsByCode,
    allTrips:
      sectionOrdering.allTrips.length > 0
        ? sectionOrdering.allTrips
        : normalized,
    joinAnAdventure:
      sectionOrdering.joinAnAdventure.length > 0
        ? sectionOrdering.joinAnAdventure
        : pickByCodes(joinAnAdventureCodes),
    familyTrips:
      sectionOrdering.familyTrips.length > 0
        ? sectionOrdering.familyTrips
        : pickByCodes(familyTripCodes),
    tripsWithPromotions:
      sectionOrdering.tripsWithPromotions.length > 0
        ? sectionOrdering.tripsWithPromotions
        : defaultPromotions,
  };
}

function buildSectionOrdering(sections, tripsById) {
  const defaults = {
    allTrips: [],
    joinAnAdventure: [],
    familyTrips: [],
    tripsWithPromotions: [],
  };

  if (!Array.isArray(sections)) {
    return defaults;
  }

  return sections.reduce((acc, section) => {
    const key = mapSectionKey(section.section);
    if (!key) {
      return acc;
    }

    const orderedTrips = (section.items ?? [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((entry) => tripsById[entry.tripId])
      .filter(Boolean);

    if (orderedTrips.length > 0) {
      acc[key] = orderedTrips;
    }

    return acc;
  }, defaults);
}

function mapSectionKey(section = "") {
  switch (section) {
    case "ALL_TRIPS":
      return "allTrips";
    case "JOIN_AN_ADVENTURE":
      return "joinAnAdventure";
    case "FAMILY_TRIPS":
      return "familyTrips";
    case "TRIPS_WITH_PROMOTIONS":
      return "tripsWithPromotions";
    default:
      return null;
  }
}

function normalizeTrip(item) {
  const overview = item.overview ?? {};
  const highlights = (item.highlights ?? [])
    .map((highlight) => highlight.text)
    .filter(Boolean);
  const promotionText = item.promotionText ?? overview.promotionText;

  return {
    id: item.id,
    uuid: item.weTravelId,
    code: item.code,
    slug: item.slug,
    name: item.name,
    summary: item.summary,
    headerSrc: resolveAsset(item.headerImageUrl),
    heroMedia: item.heroMedia,
    difficulty: item.difficulty,
    promotionText,
    overview: {
      length: overview.length,
      mapSrc: resolveAsset(overview.mapImageUrl),
      cardImgSrc: resolveAsset(overview.cardImageUrl),
      sellText: overview.sellText ?? item.summary,
      overviewText: overview.overviewText ?? [],
      highlights,
      tripDates: buildTripDates(item.dates ?? []),
      note: overview.note ?? "",
      priceNote: overview.priceNote ?? item.priceNote ?? "",
      priceSection: buildPriceSection(item.pricingTiers ?? []),
      isPrivateItinerary: !item.dates || item.dates.length === 0,
      promotionText,
    },
    itinerary: (item.itineraries ?? []).map((entry) => ({
      id: entry.id,
      day: entry.dayNumber,
      logos: entry.logos ?? [],
      title: entry.title,
      content: entry.content ?? [],
    })),
    needToKnow: (item.sections ?? []).map((section) => ({
      title: section.title,
      category: section.category,
      sections: section.body?.sections ?? [],
    })),
    photos: (item.galleries ?? []).map((gallery) => gallery.imageUrl),
  };
}

function parseLocalDate(dateString) {
  // Parse date string as local date instead of UTC to avoid timezone issues
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;

  // Handle ISO date strings (YYYY-MM-DD)
  const parts = dateString.split("T")[0].split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  // Fallback to standard Date parsing
  return new Date(dateString);
}

function buildTripDates(dates) {
  const grouped = dates.reduce((acc, entry) => {
    const startDate = parseLocalDate(entry.date);
    const endDate = parseLocalDate(entry.endDate ?? entry.date);
    const year = startDate.getFullYear();
    const dateInfo = {
      id: entry.id,
      startDate,
      endDate,
      isPeakSeason: Boolean(entry.isPeakSeason),
      isSoldOut: Boolean(entry.isSoldOut),
      dateString: formatDateLabel(startDate, endDate, entry.isPeakSeason),
    };
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(dateInfo);
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([yearA], [yearB]) => Number(yearA) - Number(yearB))
    .map(([year, list]) => ({
      year: Number(year),
      dates: list.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()),
    }));
}

function formatDateLabel(startDate, endDate, isPeakSeason) {
  const startLabel = `${monthFormatter.format(
    startDate
  )} ${startDate.getDate()}`;
  const includeEndMonth = startDate.getMonth() !== endDate.getMonth();
  const endLabel = includeEndMonth
    ? `${monthFormatter.format(endDate)} ${endDate.getDate()}`
    : `${endDate.getDate()}`;
  return `${isPeakSeason ? "*" : ""}${startLabel} - ${endLabel}`;
}

function buildPriceSection(tiers) {
  const section = {};
  tiers.forEach((tier) => {
    const key = mapPricingLabel(tier.label);
    if (!key) return;
    section[key] = {
      price: tier.priceText ?? tier.priceAmount + " " + tier.currency,
      singleSupp: tier.singleSupplement
        ? `Single Supplement $${tier.singleSupplement}`
        : undefined,
      doubleOccupancy: tier.doubleOccupancy,
      validity: tier.validityNote,
    };
  });
  return section;
}

function mapPricingLabel(label = "") {
  const normalized = label.toUpperCase();
  if (normalized === "PRIMARY") return "primary";
  if (normalized === "SECONDARY") return "secondary";
  if (normalized === "PEAK") return "peakSeason";
  return null;
}

function resolveAsset(path) {
  if (!path) return null;
  if (path.startsWith("@/assets")) {
    return assetMap[path] ?? null;
  }
  return path;
}

export function resetTripsCache() {
  tripsPromise = null;
}
